/**
 * Preferred language prompt (v4.8.389).
 *
 * After sign-in, ask once when users.pref_language is empty.
 * The first list is the KYC country's languages.
 * Search and the full list stay open so another language, such as Hindi, can still be chosen.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { getSessionHeaders } from '../../lib/serverSession'
import { searchLanguages } from '../../lib/prefLanguage.js'
import { isSupported, setStoredLanguage } from '../../lib/languageResolver'
import i18n from '../../lib/i18n'

const PREF_URL = '/api/user/pref-language'

type LanguageRow = {
  code: string
  name: string
  region_group: string
  country_code: string | null
  sort_order: number
}

type PromptState = {
  kycCountry: { code: string; name: string | null } | null
  countryLanguages: LanguageRow[]
  languages: LanguageRow[]
}

export const PreferredLanguagePrompt: React.FC = () => {
  const [prompt, setPrompt] = useState<PromptState | null>(null)
  const [query, setQuery] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const headers = getSessionHeaders()
      if (!headers['X-Session-Id']) return
      try {
        const response = await fetch(PREF_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ operation: 'current' }),
        })
        if (!response.ok) return
        const data = await response.json()
        if (cancelled || data?.impersonated || !data?.needsChoice) return
        setPrompt({
          kycCountry: data.kycCountry || null,
          countryLanguages: Array.isArray(data.countryLanguages) ? data.countryLanguages : [],
          languages: Array.isArray(data.languages) ? data.languages : [],
        })
      } catch {
        // Leave the dashboard usable. The prompt is loaded again on the next sign-in.
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!prompt) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [prompt])

  const searching = query.trim().length > 0
  const countryName = prompt?.kycCountry?.name || prompt?.kycCountry?.code || ''
  const hasCountryList = (prompt?.countryLanguages.length || 0) > 0

  const visible = useMemo(() => {
    if (!prompt) return []
    if (searching) return searchLanguages(prompt.languages, query)
    if (showAll || !hasCountryList) return prompt.languages
    return prompt.countryLanguages
  }, [prompt, query, searching, showAll, hasCountryList])

  const save = async () => {
    if (!selected || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const headers = getSessionHeaders()
      const response = await fetch(PREF_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'save', language: selected }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data?.success) {
        setSaveError(data?.error || 'We could not save your language. Please try again.')
        return
      }
      if (isSupported(selected)) {
        setStoredLanguage(selected, { explicit: true })
        try { await i18n.changeLanguage(selected) } catch { /* catalog may still be English */ }
      }
      setPrompt(null)
    } catch {
      setSaveError('We could not save your language. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!prompt || typeof document === 'undefined') return null

  const listLabel = searching
    ? 'Search results'
    : (showAll || !hasCountryList)
      ? 'All languages'
      : `Languages for ${countryName || 'your KYC country'}`

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10060,
        background: 'rgba(0, 0, 0, 0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="pref-language-title"
        style={{
          width: 'min(560px, 100%)',
          maxHeight: 'min(88vh, 820px)',
          display: 'flex',
          flexDirection: 'column',
          background: '#111827',
          color: '#f9fafb',
          border: '1px solid rgba(251, 191, 36, 0.45)',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
        }}
      >
        <div style={{ padding: '24px 24px 8px' }}>
          <p style={{ margin: '0 0 8px', color: '#d4af37', fontSize: '0.8rem', letterSpacing: '0.04em' }}>
            LANGUAGE
          </p>
          <h2 id="pref-language-title" style={{ margin: '0 0 8px', fontSize: '1.35rem', lineHeight: 1.3, color: '#fbbf24' }}>
            Choose your language
          </h2>
          <p style={{ margin: 0, color: '#e5e7eb', fontSize: '0.95rem', lineHeight: 1.5 }}>
            {hasCountryList
              ? `These languages match ${countryName || 'the country on your KYC'}. Search if you speak a different language.`
              : countryName
                ? `${countryName} has no language list of its own. Search the full list and choose the language you speak.`
                : 'Your KYC country is not on file yet. Search the full list and choose the language you speak.'}
          </p>
          <label htmlFor="pref-language-search" style={{ display: 'block', marginTop: 16, fontSize: '0.85rem', color: '#d1d5db' }}>
            Search all languages
          </label>
          <input
            id="pref-language-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Hindi, French, isiZulu..."
            autoComplete="off"
            style={{
              width: '100%',
              marginTop: 6,
              boxSizing: 'border-box',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.16)',
              background: '#0b1220',
              color: '#f9fafb',
              padding: '10px 12px',
              fontSize: '0.95rem',
            }}
          />
          {hasCountryList ? (
            <button
              type="button"
              onClick={() => { setShowAll((open) => !open); setQuery('') }}
              style={{
                marginTop: 10,
                background: 'transparent',
                color: '#fbbf24',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '0.9rem',
                textDecoration: 'underline',
              }}
            >
              {showAll ? `Show ${countryName || 'country'} languages` : 'Show full list'}
            </button>
          ) : null}
        </div>
        <div style={{ overflow: 'auto', minHeight: 0, flex: '1 1 auto', padding: '8px 24px 12px' }}>
          <p style={{ margin: '0 0 8px', color: '#9ca3af', fontSize: '0.8rem' }}>{listLabel}</p>
          {visible.length === 0 ? (
            <p style={{ margin: 0, color: '#e5e7eb' }}>No language matches that search.</p>
          ) : (
            <div role="listbox" aria-label={listLabel} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visible.map((lang) => {
                const active = selected === lang.code
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => setSelected(lang.code)}
                    style={{
                      textAlign: 'left',
                      borderRadius: 10,
                      border: active ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.12)',
                      background: active ? 'rgba(251, 191, 36, 0.12)' : '#0b1220',
                      color: '#f9fafb',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <span>{lang.name}</span>
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{lang.region_group}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', padding: '14px 24px 18px' }}>
          {saveError ? (
            <p style={{ margin: '0 0 10px', color: '#fecaca', fontSize: '0.9rem' }}>{saveError}</p>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving || !selected}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 16px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: saving || !selected ? 'not-allowed' : 'pointer',
              background: saving || !selected ? '#4b5563' : '#fbbf24',
              color: '#111827',
            }}
          >
            {saving ? 'Saving...' : 'Save language'}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  )
}

export default PreferredLanguagePrompt
